import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, Skeleton, SkeletonText, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {RichTextContent, richTextToHtml} from '@amblique/contentful/app/components/rich-text-content';
import AnimatedText from '@amblique/king-living/app/components/motion/animated-text';
import {useRichTextLinks} from '@amblique/king-living/app/contentful/hooks/use-rich-text-links';
import {hasEmbeddedContent} from '@amblique/king-living/app/contentful/utils/rich-text';

/**
 * @override
 * Custom implementation for RichTextBlock component
 * - Renders Contentful rich text content from JSON
 * - Lazy loads links for embedded entries/assets to avoid query complexity
 * - Shows skeleton loading state while fetching additional data
 * - Supports animation variants: "fade-up", "highlight-word", or null
 *
 * @param {Object} props - Component props
 * @param {Contentful.RichTextBlock} props.component - RichTextBlock component data
 * @param {'fade-up'|'highlight-word'|null} [props.component.animation] - Animation type selector
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const RichTextBlock = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/rich-text-block', {component, isComplete, isFetching});

  const {content, animation} = component;
  const isAnimationEnabled = Boolean(animation);
  const hasContent = content?.json;

  // Check if we need to fetch links (only if content has embedded entries/assets and no links provided)
  const needsLinks = useMemo(() => {
    if (!hasContent || content?.links) return false;

    return hasEmbeddedContent(content.json);
  }, [hasContent, content]);

  // Lazy load links only when needed
  const {links, isLoading: isLoadingLinks} = useRichTextLinks(component.sys?.id, {
    enabled: needsLinks,
  });

  // Merge content with fetched links
  const contentWithLinks = useMemo(() => {
    if (!hasContent) return content;
    if (content?.links) return content; // Already has links
    if (!links) return content;

    return {
      ...content,
      links,
    };
  }, [content, links, hasContent]);

  // Only compute HTML for animation mode (RichTextContent handles its own conversion)
  const html = isAnimationEnabled ? richTextToHtml(contentWithLinks) : null;

  // Show loading state if fetching links
  const isLoading = (!isComplete && isFetching) || (needsLinks && isLoadingLinks);

  return (
    (isLoading || hasContent) && (
      <Box {...styles.root} {...props}>
        {isLoading ? (
          <>
            <Skeleton height="32px" mb={4} width="60%" />
            <SkeletonText noOfLines={5} spacing={4} />
          </>
        ) : isAnimationEnabled ? (
          <Box className="rich-text-block-animation-container" {...styles.animationContainer}>
            <AnimatedText
              html={html}
              variant={animation === 'fade-up' ? 'fade-up-text' : 'highlight-rich-text'}
            />
          </Box>
        ) : (
          <RichTextContent content={contentWithLinks} {...styles.markup} />
        )}
      </Box>
    )
  );
};

RichTextBlock.displayName = 'RichTextBlock';
RichTextBlock.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
